/**
 * Stripe-Billing-Anzeige & Zahlungsstop-Sperre.
 *
 * WICHTIG: STRIPE_PAYMENT_LOCK_ARMED bleibt false, solange Stripe nicht live
 * angebunden ist — sonst würden Mandanten fälschlich ausgesperrt.
 */

/** Scharfschaltung der Zugangssperre — erst true, wenn Stripe-Webhooks live laufen. */
export const STRIPE_PAYMENT_LOCK_ARMED = false;

export type StripeBillingKind =
    | 'fehlt'
    | 'verbunden'
    | 'zahlung_aktiv'
    | 'zahlungsstop'
    | 'gekuendigt'
    | 'pro_bono';

export type StripeBillingBadge = {
    kind: StripeBillingKind;
    /** Kurzlabel ohne „Stripe:“-Prefix */
    label: string;
    /** Volle Badge-Zeile */
    display: string;
    className: string;
    /** Wie Sentinel: kritisches Zahlungsproblem */
    isCritical: boolean;
    /** Würde sperren, wenn ARMED=true */
    wouldLock: boolean;
};

const flagTrue = (v: unknown): boolean =>
    v === true || v === 1 || v === '1' || v === 'true';

const readField = (client: any, key: string): any => {
    if (!client) return undefined;
    if (client[key] !== undefined && client[key] !== null) return client[key];
    if (client.clientData?.[key] !== undefined && client.clientData?.[key] !== null) {
        return client.clientData[key];
    }
    return undefined;
};

/** Status-Strings, die ein Zahlungsproblem bedeuten (Einzug fehlgeschlagen / Rückbuchung). */
const PROBLEM_STATUSES = new Set([
    'past_due',
    'unpaid',
    'incomplete_expired',
    'payment_failed',
    'failed',
    'chargeback',
    'disputed',
    'requires_payment_method',
]);

/** Verbunden, aber noch kein laufender Einzug. */
const CONNECTED_STATUSES = new Set([
    'connected',
    'setup_complete',
    'incomplete',
    'trialing',
    'pending',
    'not_started',
]);

export const normalizeStripeStatus = (raw: unknown): string =>
    String(raw || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');

/**
 * Liest Stripe-/Billing-Felder aus Root oder clientData.
 */
export const readStripeBillingSource = (client: any) => {
    const status = normalizeStripeStatus(
        readField(client, 'stripeStatus') ?? readField(client, 'subscriptionStatus')
    );
    const setupComplete = flagTrue(readField(client, 'stripeSetupComplete'));
    const customerId = String(
        readField(client, 'stripeCustomerId') || readField(client, 'stripeCustomer') || ''
    ).trim();
    const proBono = flagTrue(readField(client, 'proBonoActive'));
    const bypass = flagTrue(readField(client, 'stripePaymentLockBypass'));
    return { status, setupComplete, customerId, proBono, bypass };
};

/**
 * Einheitliche Badge-Auflösung für Admin/Coach-CRM.
 * latestYearStatus: optionaler Fallback (Legacy „aktiv“ ohne Stripe-Feld).
 */
export const resolveStripeBillingBadge = (
    client: any,
    opts?: { latestYearStatus?: string | null }
): StripeBillingBadge => {
    const { status, setupComplete, customerId, proBono, bypass } = readStripeBillingSource(client);

    if (proBono) {
        return {
            kind: 'pro_bono',
            label: 'Pro Bono',
            display: 'Pro Bono (kein Stripe)',
            className: 'bg-pink-100 text-pink-800 border-pink-300 dark:bg-pink-500/10 dark:text-pink-400 dark:border-pink-500/20',
            isCritical: false,
            wouldLock: false,
        };
    }

    if (PROBLEM_STATUSES.has(status)) {
        const label =
            status === 'chargeback' || status === 'disputed'
                ? 'Rückbuchung'
                : status === 'past_due' || status === 'unpaid'
                  ? 'Nicht abgebucht'
                  : 'Zahlungsstop';
        return {
            kind: 'zahlungsstop',
            label,
            display: `Stripe ${label}`,
            className:
                'bg-red-100 text-red-800 border-red-400 dark:bg-red-500/20 dark:text-red-400 dark:border-red-500/40 shadow-[0_0_12px_rgba(239,68,68,0.35)] animate-pulse',
            isCritical: true,
            wouldLock: !bypass,
        };
    }

    if (status === 'canceled' || status === 'cancelled') {
        return {
            kind: 'gekuendigt',
            label: 'Gekündigt',
            display: 'Stripe Gekündigt',
            className: 'bg-stone-200 text-stone-700 border-stone-400 dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700',
            isCritical: false,
            wouldLock: false,
        };
    }

    if (status === 'active' || status === 'paid' || status === 'zahlung_aktiv') {
        return {
            kind: 'zahlung_aktiv',
            label: 'Zahlung Aktiv',
            display: 'Stripe Zahlung Aktiv',
            className: 'bg-indigo-100 text-indigo-900 border-indigo-300 dark:bg-indigo-500/20 dark:text-indigo-300 dark:border-indigo-500/30',
            isCritical: false,
            wouldLock: false,
        };
    }

    if (
        CONNECTED_STATUSES.has(status) ||
        setupComplete ||
        (!!customerId && status !== 'active')
    ) {
        return {
            kind: 'verbunden',
            label: 'Verbunden',
            display: 'Stripe verbunden',
            className: 'bg-sky-100 text-sky-900 border-sky-300 dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-500/30',
            isCritical: false,
            wouldLock: false,
        };
    }

    // Legacy: Jahr „aktiv“, aber noch kein Stripe-Feld → nicht als Zahlung Aktiv verkaufen
    const yearAktiv = String(opts?.latestYearStatus || '').toLowerCase() === 'aktiv';
    if (yearAktiv && !status) {
        return {
            kind: 'verbunden',
            label: 'Verbunden',
            display: 'Stripe verbunden (Legacy)',
            className: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20',
            isCritical: false,
            wouldLock: false,
        };
    }

    return {
        kind: 'fehlt',
        label: 'Fehlt',
        display: 'Stripe fehlt',
        className: 'bg-amber-100 text-amber-950 border-amber-500 dark:bg-yellow-500/10 dark:text-yellow-500 dark:border-yellow-500/20',
        isCritical: false,
        wouldLock: false,
    };
};

/**
 * Ob der Mandant wegen Zahlungsstop gesperrt werden soll.
 * Solange STRIPE_PAYMENT_LOCK_ARMED=false → immer false.
 */
export const shouldEnforcePaymentLock = (input: {
    client?: any;
    isGodMode?: boolean;
    isTeamUser?: boolean;
}): boolean => {
    if (!STRIPE_PAYMENT_LOCK_ARMED) return false;
    if (input.isGodMode || input.isTeamUser) return false;
    const badge = resolveStripeBillingBadge(input.client);
    return badge.wouldLock === true;
};
